import * as React from 'react';
import BaseButton, { BaseButtonProps } from '@tcl-boron-foundations/base-button';
import { getButtonColorsForButtonType } from './theme';
import Loader from '@tcl-boron-prefabs/loader';
import { uniqueId } from 'lodash-es';
import { useState } from 'react';

export interface ButtonProps
  extends Omit<
    BaseButtonProps,
    'focusColors' | 'pressedColors' | 'defaultColors' | 'hoverColors' | 'disabledColors' | 'minWidth'
  > {
  /** Control the expression of this button's style by swapping the background and text colors depending on button type. */
  buttonType?: 'primary' | 'secondary' | 'tertiary';
  /**
   * Minimum width of the button defaults to 100px but can be overridden if needed
   */
  minWidth?: string;
  /** If true, disable all user interaction for the Button. */
  disabled?: boolean;
  /** Object to edit loader properties within a button. Accepts isComplete boolean and Icon properties */
  loaderProps?: ButtonLoaderProps;
  /** label passed to the button for screen reader use */
  ariaLabel: string;
}
export interface ButtonLoaderProps {
  isComplete?: boolean;
  Icon?: React.ComponentType<{ className?: string; height?: string | number; width?: string | number; color: string }>;
}

export const Button: React.FC<ButtonProps> = ({
  buttonType = 'primary',
  minWidth = '100px',
  disabled = false,
  small = false,
  loaderProps = { isComplete: false, Icon: undefined },
  ariaLabel,
  id,
  ...props
}) => {
  [id] = useState(id || uniqueId('button_'));

  const { pressedColors, focusColors, hoverColors, defaultColors, disabledColors } =
    getButtonColorsForButtonType(buttonType);
  const loader = (
    <Loader
      size={loaderProps.Icon !== undefined ? 'large' : small ? 'small' : 'large'}
      loaderType={buttonType === 'secondary' ? 'secondary' : 'primary'}
      isComplete={loaderProps.isComplete}
      Icon={loaderProps.Icon}
    />
  );

  return (
    <BaseButton
      ariaLabel={ariaLabel}
      Loader={loader}
      disabledColors={disabledColors}
      pressedColors={pressedColors}
      focusColors={focusColors}
      data-analytics="button"
      defaultColors={defaultColors}
      hoverColors={hoverColors}
      minWidth={minWidth}
      disabled={disabled}
      small={small}
      key={id}
      id={id}
      {...props}
    />
  );
};

Button['isButton'] = true;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isButton(component: any): component is React.ReactElement<ButtonProps> {
  return component.type?.isButton === true;
}
